<template>
  <b-modal
    id="measure-apply"
    size="xl"
    @show="showModal"
    @hide="onHide"
    @close="onHide"
    centered
    class="egx-partner-color custom-modal app"
    hide-footer
    hide-header
  >
    <div class="content-box-plus">
      <h4 class="title pb-3">{{ pageConfiguration?.title || 'Measure Details' }}</h4>
      <div class="m-2 text-center" v-if="isLoading">
        <font-awesome-icon
          :icon="['fa', 'circle-notch']"
          class="fa-spin m-auto spinner fa-5x egx-partner-color"
        />
      </div>

      <div v-else>
        <ValidationObserver ref="measureSave" v-if="selectedMeasure">
          <div class="content-box-plus child box d-grid pr-4 col-12 my-3">
            <h4 class="title">
              {{ selectedMeasure.name }}
            </h4>
            <h5 class="description-large">
              {{ selectedMeasure.hierarchy_level_1 }}
            </h5>
            <div>
              <span class="measure-description pt-2 description-large" v-safe-html="selectedMeasure.program_specific_measure_details" />
              <div class="container mt-4">
                <div class="row">
                  <div class="col-md-12 col-lg-6 py-2 align-content-center">
                    <div class="row mb-2 form">
                      <div class="col-md-6 p-0 align-self-center text-align-end">
                        <div class="m-auto field-label-parent">
                          <label class="field-label m-0">
                            Quantity {{ selectedMeasure.units_qty ? `(${selectedMeasure.units_qty})` : '' }}
                            <span class="text-danger">*</span>
                          </label>
                        </div>
                      </div>
                      <div class="col-md-6 pl-0 align-content-center">
                        <validation-provider :rules="'required|decimal|min_value:1'" v-slot="{ errors }">
                          <input
                            type="number"
                            name="quantity"
                            v-model="fieldValues['quantity']"
                            id="quantity"
                            style="width: 6rem; height: 2rem; margin: 0.2rem 0;"
                            class="input-text form-control"
                            :class="{ 'box-alert': errors[0] }"
                            @keydown="blockInvalidChars"
                          />
                          <span class="error-message" v-if="errors[0]" :class="errors[0] ? 'has-error' : ''">{{ errMsg = errors[0] }}</span>
                        </validation-provider>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-for="(fieldGroup, index) in fieldGroups" :key="fieldGroup.id">
            <div class="content-box-plus child box row m-0">
              <div class="mt-2 title col-12 pl-0">
                {{ pageSettings?.title || 'Measure Installation Details' }}
              </div>
              <div class="description-large">
                {{ pageSettings?.field_section_description || 'Please tell us about the installation details.' }}
              </div>
              <div class="container mt-4">
                <div class="row">
                  <div class="col-md-12 col-lg-6 py-2 align-content-center" v-for="(field, index) in fieldGroup.fields" :key="field.name">
                    <div class="row mb-2 form">
                      <div class="col-md-6 p-0 align-self-center text-align-end">
                        <div class="m-auto field-label-parent">
                          <label :for="field.name" class="field-label m-0">
                            {{  field.name !== '$' ? field.label : ' ' }}
                            <span class="text-danger" v-if="field.required === 'true'">*</span>
                            <template v-if="field.name !== '$' && field.inline_help !== ''">
                            <span class="field-tooltip">
                              <font-awesome-icon
                                :icon="['fa', 'question-circle']"
                                :id="`tooltip-${index}`"
                                class="egx-partner-color ml-2"
                              />
                            </span>
                              <b-popover
                                :target="`tooltip-${index}`"
                                triggers="hover"
                                placement="top"
                              >
                                {{ field.inline_help }}
                              </b-popover>
                            </template>
                          </label>
                        </div>
                      </div>
                      <div class="col-md-6 pl-0 align-content-center">
                        <validation-provider
                          :rules="addValidation(field)"
                          v-slot="{ errors }"
                          :ref="'ql-item-' + field.name"
                          :custom-messages="customErrorMsg"
                        >
                          <toggle-button
                            v-model="fieldValues[field.name]"
                            :name="field.name"
                            :id="field.name"
                            :width="62"
                            :height="25"
                            :font-size="15"
                            :labels="{ checked: 'Yes', unchecked: 'No' }"
                            v-if="field.type === 'BOOLEAN'"
                          />

                          <b-input-group v-else-if="field.type.toLowerCase() === 'date'">
                            <b-form-input
                              :id="field.name"
                              v-model="fieldValues[field.name]"
                              :name="field.name"
                              type="text"
                              class="input-text d-none"
                            />
                            <b-input-group-append class="w-100">
                              <b-form-datepicker class="input-text" today-button reset-button close-button v-model="fieldValues[field.name]" />
                            </b-input-group-append>
                          </b-input-group>

                          <div v-else-if="field.type === 'multi_select'">
                            <multiselect
                              v-model="fieldValues[field.name]"
                              tag-placeholder="Add this as new option"
                              placeholder="Select all that apply."
                              :options="getOptions(field.rules)"
                              :multiple="true"
                              :taggable="true"
                            />
                            <input
                              type="text"
                              :name="field.name"
                              v-model="fieldValues[field.name]"
                              :id="field.name"
                              class="input-text form-control multi-select d-none"
                            />
                          </div>
                          <div v-else-if="field.type === 'Specific values'">
                            <multiselect
                              v-model="fieldValues[field.name]"
                              :options="getOptions(field.rules)"
                              :placeholder="field.required === 'true' ? getPlaceHolder(field.rules) : 'None'"
                              :multiple="false"
                              :taggable="false"
                            />
                            <input
                              type="text"
                              :name="field.name"
                              v-model="fieldValues[field.name]"
                              :id="field.name"
                              class="input-text form-control single-select d-none"
                            />
                          </div>
                          <input
                            type="number"
                            :name="field.name"
                            v-model="fieldValues[field.name]"
                            :id="field.name"
                            class="input-text form-control"
                            v-else-if="field.type === 'Numeric'"
                            @keydown="blockInvalidChars"
                          />
                          <textarea
                            :name="field.name"
                            v-model="fieldValues[field.name]"
                            :id="field.name"
                            class="input-text form-control"
                            v-else-if="field.type === 'Free-form'"
                          />
                          <input
                            type="text"
                            :name="field.name"
                            v-model="fieldValues[field.name]"
                            :id="field.name"
                            class="input-text form-control"
                            v-else-if="field.type === 'Free-form (single line)'"
                          />
                          <input
                            type="text"
                            :name="field.name"
                            v-model="fieldValues[field.name]"
                            :id="field.name"
                            class="input-text form-control"
                            v-else-if="field.type === 'Currency'"
                                @input="onCurrencyInput(field.name)"
                          />

                          <span class="error-message" v-if="errors[0]" :class="errors[0] ? 'has-error' : ''">{{ errMsg = errors[0] }}</span>
                        </validation-provider>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="button mt-4 text-center">
            <div @click="$bvModal.hide('measure-apply')" class="btn btn-egx-white mr-3">
              Cancel
            </div>
            <div @click="saveMeasure()" class="btn btn-egx">
             {{ selectedRetrofit && !isDuplicate ? 'Save Changes' : 'Add to Application' }}
            </div>
          </div>
        </ValidationObserver>
      </div>
    </div>
  </b-modal>
</template>

<script>
import Multiselect from 'vue-multiselect';
import FieldHelper from '@/shared/mixins/field-helper';
import AccordionTabList from '@/components/project/AccordionTabList.vue';
import Vue from 'vue';

export default {
  name: 'MeasureApplyModal',
  components: {
    Multiselect,
    AccordionTabList
  },

  mixins: [ FieldHelper ],

  props: {
    selectedMeasure: Object,
    projectId: String,
    selectedRetrofit: Object,
    fulfillSyncDetails: Object,
    isDuplicate: Boolean,
  },

  data() {
    return {
      isLoading: false,
      fieldGroups: [],
      measureType: {},
      fieldValues: {},
      customErrorMsg: {},
      errMsg: {},
      retrofit: {},
      pageConfiguration: {},
    };
  },

  created() {
    this.$store.dispatch('fulfill_integrate/getPageConfiguration', {
      task_name: 'measureinfo',
      project_id: this.$route.params.id,
    })
    .then((response) => {
      this.pageConfiguration = response;
      console.log('Page Configuration:', this.pageConfiguration);
    })
    .catch((error) => {
      console.log(error);
    });
  },

  computed: {
    pageSettings() {
      return this.fulfillSyncDetails?.pages?.measure?.application_details;
    }
  },

  methods: {
    showModal() {
      this.isLoading = true;

      const params = {
        project_id: this.projectId,
        measure_type_name: this.selectedMeasure.type,
        program_measure_id: this.selectedMeasure.program_measure_id,
      }

      this.fieldValues = {};
      this.fieldGroups = [];
      this.measureType = {};

      if (this.selectedRetrofit) {
        this.retrofit = this.selectedRetrofit;

        this.retrofit.fields.forEach((field) => {
          if (field.type.toLowerCase() === 'numeric' && field.value) {
            field.value = parseInt(field.value);
          } else if (field.type.toLowerCase() === 'currency' && field.value) {
            field.value = parseFloat(field.value);
          } else if (field.type.toLowerCase() === 'boolean' && field.value) {
            field.value = field.value === 'true' || field.value === true;
          }

          this.fieldValues[field.name] = field.value;
        });
        this.fieldValues['quantity'] = this.retrofit.quantity;
      } else {
        this.retrofit = {
          type: this.selectedMeasure.type,
          program_measure_id: this.selectedMeasure.program_measure_id,
          name: this.selectedMeasure.name,
          id: this.selectedMeasure.id,
          status: 'Included',
          quantity: 0,
          fields: [],
        }
      }

      this.$store.dispatch('fulfill_integrate/get_measures_type', { ...params })
        .then((response) => {
          this.fieldGroups = response.field_groups;
          this.measureType = response.measure_type;

          if (!this.selectedRetrofit || this.isDuplicate) {
            this.retrofit['pm_id'] = response.uuid;
            this.fieldValues['quantity'] = this.selectedMeasure.default_quantity || 0;

            this.fieldGroups.forEach((group) => {
              group.fields.forEach((field) => {
                if (field.type !== 'Spacer') {
                  this.retrofit.fields.push({
                    type: field.type,
                    object_name: field.object_name,
                    name: field.name,
                    field_group_id: field.field_group_id,
                    value: this.isDuplicate ? this.fieldValues[field.name] || '' : '',
                  });

                  if (!this.isDuplicate && field.default_value) {
                    let defaultValue = field.default_value;
                    if (field.type.toLowerCase() === 'boolean') {
                      defaultValue = field.default_value === 'true';
                    }

                    this.fieldValues[field.name] = defaultValue;
                  }
                }
              });
            });
          }
        }).catch((error) => {
          console.log(error);
        }).finally(() =>{
          this.isLoading = false;
      });
    },

    saveMeasure() {
      this.$refs.measureSave.validate()
        .then((isValid) => {
          if (isValid) {
            this.isLoading = true;

            this.retrofit.quantity = this.fieldValues.quantity;
            this.retrofit.fields = this.retrofit.fields.map((field) => {
              const value = this.fieldValues[field.name];
              field.value = value !== undefined && value !== null ? `${value}` : "";
              return field;
            });

            const data = {
              details: {
                isFieldUpdate: true,
                project_id: this.projectId,
                retrofit: this.retrofit,
              }
            };

            this.$store.dispatch('fulfill_integrate/post', data).then(async (response) => {
              this.$notify({
                group: 'notice',
                text: response.message,
              });

              await this.$store.dispatch('fulfill_integrate/get_project_selected', { project_id: this.projectId });

              this.$emit('on-save');
              this.$bvModal.hide('measure-apply');
            }).catch((error) => {
              console.log(error.message);
              this.$notify({
                group: 'notice-stay',
                type: 'error',
                text: error.message,
              });
            }).finally(() => {
              this.isLoading = false;
            });
          } else {
            console.log('Not Validated.');

            this.$notify({
              group: 'notice-stay',
              type: 'error',
              text: this.fulfillSyncDetails.messages.validations,
            });

            this.$nextTick(() => {
              const hasError = document.querySelector('.has-error');
              if (hasError) {
                hasError.scrollIntoView({ block: 'center', behavior: 'smooth' });
              }
            });
          }
        });

    },

    addValidation(question) {
      const validationList = [];

      if (question.required === 'true') {
        this.customErrorMsg['required'] = "This field is required.";
        validationList.push('required');
      }

      if (question.type === 'Numeric') {
        this.customErrorMsg['numeric'] = "This field only accepts numeric values.";
        validationList.push('numeric');
      } else if (question.type === 'Float') {
        this.customErrorMsg['decimal'] = "This field only accepts decimal values.";
        validationList.push('decimal');
      } else if (question.type === 'Currency') {
        this.customErrorMsg['double'] = "This field only accepts two decimals.";
        validationList.push('double:2');
      }

      return validationList.join('|');
    },

    onHide() {
      Vue.notify({ group: 'notice-stay', clean: true });
      this.$emit('on-hide');
    },

    onCurrencyInput(fieldName) {
      const value = this.fieldValues[fieldName];
      const valid = value.replace(/[^0-9.]/g, '');
      const parts = valid.split('.');
      if (parts.length > 2) {
        this.fieldValues[fieldName] = parts[0] + '.' + parts.slice(1).join('');
      } else {
        this.fieldValues[fieldName] = valid;
      }
    }
  },
};
</script>

<style lang="scss">
@import '../../assets/stylesheets/components/project/measures.scss';
</style>
